import type {
  DiagnosticAttemptStatus,
  DiagnosticBand,
  DiagnosticFinalOutcome,
  DiagnosticGateGroup,
  DiagnosticGateQuestion,
  DiagnosticGateResult,
  DiagnosticResponseRow,
  DiagnosticResultEnvelope,
  DiagnosticResultPerSection,
  DiagnosticSection,
  DiagnosticVersionTree,
} from "../types/diagnostic";
import { isQuestionApplicable } from "./trades";

/**
 * Display names for the gate dimensions, mirroring
 * `DiagnosticQuestion::GATE_GROUP_LABELS`. An unrecognised group falls back to
 * the question's own wording.
 */
export const GATE_GROUP_LABELS: Record<string, string> = {
  org: "Org structure",
  revenue: "Revenue",
};

/**
 * Secondary score at or above this promotes one stage above the gate.
 *
 * Mirrored from `DiagnosticScoringService::GATE_PROMOTE_AT`, which is
 * authoritative. Change both or the coach sees one placement while the record
 * stores another; `gateScoring.test.ts` and its PHP counterpart assert these
 * values on each side.
 */
export const GATE_PROMOTE_AT = 2.5;

/** Secondary score below this demotes one stage. Mirrored the same way. */
export const GATE_DEMOTE_BELOW = 1.5;

/** Bar scale for an instrument that authors no ordinal choices at all. */
const DEFAULT_SCORE_CEILING = 3;

/**
 * The highest score any authored choice awards — the full scale a score bar is
 * drawn against.
 *
 * The shipped instrument scores 0–3, and both labs' rail and its PDF hardcoded
 * a `/3` divisor. Reading the ceiling from the instrument means a republished
 * ladder on a wider scale still draws bars that fit their track.
 *
 * **Gate sections are not scanned.** The number this scales is the secondary
 * score, which excludes gate sections by construction — and a gate choice's
 * `score` is not an answer score at all: it is a position on the stage ladder,
 * read by `computeGate()` as an index. A gate ladder authored deeper than the
 * scored questions' range would otherwise stretch every bar against a ceiling
 * no scored answer can reach.
 */
export function authoredScoreCeiling(
  sections: Pick<DiagnosticSection, "questions" | "isGate">[],
): number {
  const scores = sections
    .filter((s) => !s.isGate)
    .flatMap((s) =>
      s.questions.flatMap((q) => (q.choices ?? []).map((c) => c.score)),
    );
  return scores.length > 0 ? Math.max(...scores, 1) : DEFAULT_SCORE_CEILING;
}

/**
 * The one number a screen or a PDF may print as "the score", with D15's null
 * intact — for gated **and** ungated instruments.
 *
 * A gated instrument carries `gate.secondaryScore`, which is null when nothing
 * scoreable has been answered and a real `0` when the scored answers genuinely
 * average zero. An ungated one has no gate at all, so the only field on the
 * envelope is `weightedTotal` — and `weightedTotal` is `secondaryScore ?? 0`,
 * which collapses exactly that distinction. `DiagnosticResultEnvelope`'s note
 * therefore forbade the only field the ungated branches had, and they went on
 * reading it: three review rounds re-fixed the gated path and left this one.
 *
 * The distinction is recoverable without an envelope change, because the rows
 * the total was computed over travel with it. `computeWeightedTotal()` returns
 * null exactly when no scored, non-gate section contributed any weight — which
 * is what this reads off `perSection`. The *number* still comes from the
 * envelope; only its nullability is derived, so the backend stays the single
 * source of the arithmetic.
 *
 * Gate sections are excluded by id because `perSection` carries `isScored` but
 * not `isGate`; callers read the flag off the version tree, as they already do
 * for the score breakdown. Passing none is correct for an instrument with no
 * gate section, which is every instrument that reaches the ungated branch.
 */
export function headlineScore(
  result: Pick<
    DiagnosticResultEnvelope,
    "gate" | "perSection" | "weightedTotal"
  >,
  gateSectionIds: Iterable<number> = [],
): number | null {
  if (result.gate) return result.gate.secondaryScore;

  const gateSections = new Set(gateSectionIds);
  const contributedWeight = result.perSection.reduce(
    (total, ps) =>
      ps.isScored && ps.rawAvg != null && !gateSections.has(ps.sectionId)
        ? total + ps.weight
        : total,
    0,
  );

  return contributedWeight > 0 ? result.weightedTotal : null;
}

export interface ScoreInputs {
  version: Pick<DiagnosticVersionTree, "sections" | "bands">;
  responses: DiagnosticResponseRow[];
  status: DiagnosticAttemptStatus;
  /**
   * Trades the business operates. Questions tagged with any other trade are
   * neither asked nor scored. Defaults to none, which is correct for
   * diagnostics that tag nothing by trade.
   */
  trades?: string[];
}

/**
 * PHP casts every gate choice score with `(int)`. Today all gate scores are
 * whole numbers 0–3 so the two engines agree either way, but an authored 2.5
 * would split them and the coach would be shown a stage the database never
 * recorded. Truncating here keeps the mirror exact by construction.
 *
 * `Math.trunc` — not `Math.floor` — because PHP's `(int)` cast truncates
 * toward zero.
 */
const gateScore = (score: number): number => Math.trunc(score);

/**
 * Single-phase scoring mirror (matches the authoritative PHP service).
 *
 *   ready iff band.outcomeKey === 'ready'
 *
 * Only questions applicable to the selected trades are scored — a question a
 * business was never asked must not drag its average down. `yes_no` questions
 * never contribute to score; private sections still count toward the weighted
 * total when scored — the private flag only controls what the member sees.
 */
export function scoreDiagnostic(inputs: ScoreInputs): DiagnosticResultEnvelope {
  const { version, responses, status, trades = [] } = inputs;

  const responsesByQuestion = new Map<number, DiagnosticResponseRow>();
  for (const r of responses) {
    responsesByQuestion.set(r.questionId, r);
  }

  const perSection: DiagnosticResultPerSection[] = version.sections.map((s) =>
    scoreSection(s, responsesByQuestion, trades),
  );

  const gateSections = version.sections.filter((s) => s.isGate);
  const gateSectionIds = new Set(gateSections.map((s) => s.id));

  // Gate sections set the ceiling and never feed the secondary score.
  // `null` here means "no scored section contributed any weight", which is not
  // the same as an average that genuinely worked out to zero — see D15.
  const secondaryScore = computeWeightedTotal(
    perSection.filter((ps) => !gateSectionIds.has(ps.sectionId)),
  );

  // The reported total stays a plain number, and stays 0 when nothing has been
  // scored yet. The null is an internal distinction the gate consumes; the
  // payload shape the rail, the company detail table and the PDF read is
  // unchanged.
  const weightedTotal = secondaryScore ?? 0;

  const gate =
    gateSections.length > 0
      ? computeGate(gateSections, responsesByQuestion, secondaryScore, trades)
      : null;

  // With a gate, placement is by final stage index rather than raw total.
  const bandLookup = gate ? gate.finalStage : weightedTotal;

  const band = bandLookup != null ? pickBand(bandLookup, version.bands) : null;
  const finalOutcome = computeFinalOutcome(status, band);

  return {
    perSection,
    weightedTotal,
    band: band
      ? { id: String(band.id), label: band.label, outcomeKey: band.outcomeKey }
      : null,
    finalOutcome,
    gate,
  };
}

/**
 * Gate placement — mirrors `DiagnosticScoringService::computeGate()`.
 *
 * Applicable gate questions are first collapsed by gate group: the per-trade
 * revenue answers average (rounded) into one revenue stage. The lowest group
 * stage is then the ceiling, and the secondary score moves it one stage at
 * most, clamped to the range the authored choices allow.
 *
 * A question with no gate group is its own group, which reproduces the
 * "lowest single answer wins" rule every pre-multi-trade version relies on.
 *
 * Until every applicable gate question is answered there is no placement, so
 * `gateStage` and `finalStage` are null and no adjustment applies — but the
 * secondary score is reported as computed (D20). The gate being open is not
 * evidence about the scored sections.
 *
 * `secondaryScore` of `null` means no scored section has contributed any
 * weight yet — nothing scoreable has been answered — and no adjustment applies
 * (D15). A real `0` is a genuine average and still demotes.
 */
function computeGate(
  gateSections: DiagnosticSection[],
  responsesByQuestion: Map<number, DiagnosticResponseRow>,
  secondaryScore: number | null,
  trades: string[],
): DiagnosticGateResult {
  const questions: DiagnosticGateQuestion[] = [];
  const groups = new Map<string, { label: string; stages: number[] }>();
  let unanswered = false;
  let maxStage = 0;

  for (const section of gateSections) {
    for (const q of section.questions) {
      if (q.type !== "ordinal_mc") continue;
      if (!isQuestionApplicable(q, trades)) continue;
      for (const c of q.choices ?? []) {
        maxStage = Math.max(maxStage, gateScore(c.score));
      }
      const r = responsesByQuestion.get(q.id);
      const choice =
        r && r.ordinalChoiceId != null
          ? q.choices?.find((c) => c.id === r.ordinalChoiceId)
          : undefined;
      const stage = choice ? gateScore(choice.score) : null;

      const groupKey = q.gateGroup ?? `question-${q.id}`;
      let group = groups.get(groupKey);
      if (!group) {
        group = {
          label: GATE_GROUP_LABELS[q.gateGroup ?? ""] ?? q.text,
          stages: [],
        };
        groups.set(groupKey, group);
      }

      if (stage == null) {
        unanswered = true;
      } else {
        group.stages.push(stage);
      }

      questions.push({
        questionId: q.id,
        text: q.text,
        stage,
        tradeTag: q.tradeTag ?? null,
        gateGroup: q.gateGroup ?? null,
      });
    }
  }

  // One stage per group: the mean of its answers, rounded to the nearest
  // stage. `Math.round` matches PHP_ROUND_HALF_UP for the non-negative stage
  // values the instrument can produce; the two differ only on negatives,
  // which no authored choice can express.
  const groupRows: DiagnosticGateGroup[] = [];
  const stages: number[] = [];
  for (const [key, group] of groups) {
    const stage =
      group.stages.length > 0
        ? Math.round(
            group.stages.reduce((sum, s) => sum + s, 0) / group.stages.length,
          )
        : null;
    if (stage != null) stages.push(stage);
    groupRows.push({ key, label: group.label, stage });
  }

  if (unanswered || stages.length === 0) {
    return {
      questions,
      groups: groupRows,
      gateStage: null,
      // The secondary score is passed straight through, not nulled (D20). An
      // outstanding gate question says nothing about whether the scored
      // sections have been answered, and forcing a null here discarded a
      // weighted total that was fully computed — the rail read "nothing scored
      // yet" over a draft whose scored sections were complete, and a mid-draft
      // PDF printed the same dash. D15's real distinction still holds: a null
      // from `computeWeightedTotal()` means nothing scoreable was answered and
      // flows through as null.
      secondaryScore,
      adjustment: "none",
      finalStage: null,
    };
  }

  const gateStage = Math.min(...stages);
  let adjustment: DiagnosticGateResult["adjustment"] = "none";
  let finalStage = gateStage;
  // A coach who has answered only the gate questions has no secondary score at
  // all. Reading that absence as a 0 demoted them below their own answers.
  if (secondaryScore != null) {
    if (secondaryScore >= GATE_PROMOTE_AT) {
      adjustment = "up";
      finalStage = Math.min(gateStage + 1, maxStage);
    } else if (secondaryScore < GATE_DEMOTE_BELOW) {
      adjustment = "down";
      finalStage = Math.max(gateStage - 1, 0);
    }
  }

  return {
    questions,
    groups: groupRows,
    gateStage,
    secondaryScore,
    adjustment,
    finalStage,
  };
}

function scoreSection(
  section: DiagnosticSection,
  responsesByQuestion: Map<number, DiagnosticResponseRow>,
  trades: string[],
): DiagnosticResultPerSection {
  let rawAvg: number | null = null;
  if (section.isScored) {
    const scores: number[] = [];
    for (const q of section.questions) {
      if (q.type !== "ordinal_mc") continue;
      // A stale answer from a deselected trade must not count — the coach can
      // no longer see the question it belongs to.
      if (!isQuestionApplicable(q, trades)) continue;
      const r = responsesByQuestion.get(q.id);
      if (!r || r.ordinalChoiceId == null) continue;
      const choice = q.choices?.find((c) => c.id === r.ordinalChoiceId);
      if (choice) scores.push(choice.score);
    }
    rawAvg =
      scores.length > 0
        ? scores.reduce((sum, s) => sum + s, 0) / scores.length
        : null;
  }

  return {
    sectionId: section.id,
    name: section.name,
    rawAvg,
    weight: section.weight,
    isPrivate: section.isPrivate,
    isScored: section.isScored,
  };
}

/**
 * The weighted average of the scored, non-gate sections.
 *
 * Returns `null` — not `0` — when no scored section contributed any weight,
 * i.e. nothing scoreable has been answered. The two cases are numerically
 * indistinguishable but mean opposite things to the gate: a genuine `0` is the
 * worst possible average and demotes, while "nothing answered yet" is no
 * evidence at all and must leave the gate stage alone (D15). Mirrors
 * `DiagnosticScoringService::computeWeightedTotal()`.
 */
function computeWeightedTotal(
  perSection: DiagnosticResultPerSection[],
): number | null {
  let weightedSum = 0;
  let weightTotal = 0;
  for (const ps of perSection) {
    if (!ps.isScored || ps.rawAvg == null) continue;
    weightedSum += ps.rawAvg * ps.weight;
    weightTotal += ps.weight;
  }
  return weightTotal > 0 ? weightedSum / weightTotal : null;
}

function pickBand(
  weightedTotal: number,
  bands: DiagnosticBand[],
): DiagnosticBand | null {
  return (
    bands.find(
      (b) => weightedTotal >= b.minScore && weightedTotal <= b.maxScore,
    ) ?? null
  );
}

function computeFinalOutcome(
  status: DiagnosticAttemptStatus,
  band: DiagnosticBand | null,
): DiagnosticFinalOutcome | null {
  if (status !== "complete") return null;
  if (!band) return "not_ready";
  return band.outcomeKey === "ready" ? "ready" : "not_ready";
}
